import { getApiUrl } from './endpoints';

const ACCESS_KEY = 'auth_token';
const REFRESH_KEY = 'auth_refresh_token';

export interface AuthTokens {
  token?: string;
  refreshToken?: string;
}

/** Session-expired signal for the UI. Never navigates by itself. */
export const SESSION_EXPIRED_EVENT = 'app:sessionExpired';

export function saveAuthSession(data: AuthTokens): void {
  try {
    if (data.token) localStorage.setItem(ACCESS_KEY, data.token);
    if (data.refreshToken) localStorage.setItem(REFRESH_KEY, data.refreshToken);
  } catch {
    // ignore storage errors (private mode / restricted environments)
  }
}

export function clearAuthSession(): void {
  try {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  } catch {
    // ignore
  }
}

export function getAccessToken(): string | null {
  try {
    return localStorage.getItem(ACCESS_KEY);
  } catch {
    return null;
  }
}

export function hasRefreshToken(): boolean {
  try {
    return !!localStorage.getItem(REFRESH_KEY);
  } catch {
    return false;
  }
}

/** How early an access token is renewed: the margin the picture URLs need. */
const REFRESH_MARGIN_MS = 2 * 60 * 1000;

/** The `exp` of a JWT in milliseconds, or null when it cannot be read. */
function getTokenExpiryMs(token: string): number | null {
  const part = token.split('.')[1];
  if (!part) return null;
  try {
    const padded = part.replace(/-/g, '+').replace(/_/g, '/')
      .padEnd(Math.ceil(part.length / 4) * 4, '=');
    const payload = JSON.parse(atob(padded));
    return typeof payload?.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

/**
 * Whether the access token is missing or about to expire.
 *
 * A token whose expiry cannot be read counts as *not* stale: the server is the
 * authority on tokens it did not mint in the shape we expect, and guessing wrong
 * here would refresh on every image.
 */
export function isAccessTokenStale(token: string | null = getAccessToken()): boolean {
  if (!token) return true;
  const expiry = getTokenExpiryMs(token);
  if (expiry === null) return false;
  return expiry - Date.now() < REFRESH_MARGIN_MS;
}

/**
 * The current access token, renewed first when it is about to expire.
 *
 * `authFetch` refreshes after a 401; a picture cannot, because `<img>` has no place
 * to hear the status back — it just fails. So the callers that build an image URL
 * ask for a live token up front instead (`useAuthFileSrc`).
 */
export async function ensureFreshAccessToken(): Promise<string | null> {
  const token = getAccessToken();
  if (token && !isAccessTokenStale(token)) return token;
  if (!hasRefreshToken()) return token;
  return refreshAccessToken();
}

let keepAliveStarted = false;

/**
 * Renew the access token before it dies, while a page sits open.
 *
 * In production an access token lasts 15 minutes. Until now nothing renewed it
 * until a request had already failed, which an `<img src="…?token=…">` cannot
 * survive: a panel opened at minute 16 showed a placeholder where the picture was.
 * Checking once a minute — and again when the tab becomes visible — keeps the token
 * in the URL alive at the moment it is written, and costs nothing while it is not.
 *
 * Idempotent: every entry point calls it, only the first call does anything.
 */
export function startSessionKeepAlive(): void {
  if (keepAliveStarted || typeof window === 'undefined') return;
  keepAliveStarted = true;

  const renewIfStale = () => {
    if (!hasRefreshToken()) return;
    if (!isAccessTokenStale()) return;
    void refreshAccessToken();
  };

  // A minute is well inside the 15-minute token and far below what would turn the
  // tab into a poller.
  window.setInterval(renewIfStale, 60_000);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') renewIfStale();
  });
  renewIfStale();
}

let refreshInFlight: Promise<string | null> | null = null;

/**
 * Mint a new access token from the stored refresh token.
 *
 * Single-flight: the server *rotates* the refresh token (the old session is
 * deleted when a new pair is issued), so parallel refreshes would invalidate
 * each other — concurrent 401s share one attempt instead.
 */
export async function refreshAccessToken(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    try {
      let refreshToken: string | null = null;
      try {
        refreshToken = localStorage.getItem(REFRESH_KEY);
      } catch {
        return null;
      }
      if (!refreshToken) return null;

      const res = await fetch(`${getApiUrl()}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) return null;

      const data = await res.json();
      if (!data?.token) return null;

      saveAuthSession(data); // rotation: keep the new access *and* refresh token
      return data.token as string;
    } catch {
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

/**
 * Fetch with the session token, refreshing silently on 401.
 *
 * Contract: this never navigates. If the refresh also fails it returns the 401
 * response and dispatches `app:sessionExpired`, so the caller can show a
 * non-destructive prompt — an expired session must not yank a user who is
 * mid-typing or mid-recording (see docs/TROUBLESHOOTING.md §2).
 *
 * Do not use for `/api/auth/login` or `/api/auth/refresh` themselves.
 */
export async function authFetch(
  input: RequestInfo | URL,
  init: RequestInit = {},
): Promise<Response> {
  const withToken = (token: string | null, base: RequestInit): RequestInit => ({
    ...base,
    headers: {
      ...((base.headers as Record<string, string> | undefined) ?? {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  let res = await fetch(input, withToken(getAccessToken(), init));

  if (res.status === 401) {
    const token = await refreshAccessToken();
    if (token) {
      res = await fetch(input, withToken(token, init));
    }
  }

  if (res.status === 401) {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
    }
  }

  return res;
}
